import { decodeErrorResult } from "viem";
import manifest from "../contracts-manifest.json";

/** Contract name (manifest key) -> full ABI array (includes `error` entries). */
const contractAbiByName: Record<string, any[]> | null = (() => {
  const contracts = (manifest as any).contracts;
  if (!Array.isArray(contracts)) return null;
  const map: Record<string, any[]> = {};
  for (const c of contracts) {
    if (Array.isArray(c?.abi)) map[c.name] = c.abi;
  }
  return map;
})();

/** Look up the full ABI (with error defs) for a known implementation. */
export function abiForContract(name: string): any[] | undefined {
  const fallback = (name.startsWith("NDNFTFactoryERC721") ? "NDNFTFactoryERC721"
    : name.startsWith("NDNFTFactoryERC1155") ? "NDNFTFactoryERC1155" : undefined);
  const arr = contractAbiByName?.[name];
  if (arr) return arr;
  return fallback ? contractAbiByName?.[fallback] : undefined;
}

/** Selector of the standard Solidity `Error(string)` revert encoding. */
const ERROR_STRING_SELECTOR = "0x08c379a0";

/**
 * Class names viem/wagmi put on `e.name` for their own wrapper errors. These are
 * NOT messages — preferring them is what made every revert read
 * "ContractFunctionExecutionError" instead of the real reason.
 */
const VIEM_WRAPPER_NAMES = new Set([
  "ContractFunctionExecutionError",
  "ContractFunctionRevertedError",
  "ContractFunctionReturnDataError",
  "CallExecutionError",
  "ExecutionRevertedError",
  "AbiFunctionNotFoundError",
  "InvalidAddressError",
  "UnknownRpcError",
  "RpcRequestError",
]);

/**
 * Decode a standard `Error(string)` revert payload to its reason string.
 *
 * The auction facets still use `require(cond, "!reason")` throughout, so the
 * overwhelmingly common on-chain revert is `Error(string)` — selector
 * 0x08c379a0 followed by an ABI-encoded string — NOT a custom `error` entry.
 * `decodeErrorResult` cannot handle it (it matches custom-error signatures and
 * throws on the standard selector), so unpack the payload directly:
 *   [4-byte selector][32-byte offset = 0x20][32-byte length][length bytes]
 */
function decodeErrorString(data: string): string | undefined {
  if (!data.startsWith(ERROR_STRING_SELECTOR)) return undefined;
  // data is "0x" + 8 hex selector chars + args. ERROR_STRING_SELECTOR is
  // "0x" + those same 8 chars, so slicing at its length leaves the args block
  // with NO "0x" prefix — word offsets start at 0.
  const body = data.slice(ERROR_STRING_SELECTOR.length);
  // body = offset word (32B) + length word (32B) + string bytes.
  if (body.length < 64 + 64) return undefined;
  if (parseInt(body.slice(0, 64), 16) !== 0x20) return undefined; // expect offset 32
  const len = parseInt(body.slice(64, 128), 16);
  if (!Number.isSafeInteger(len) || len < 0) return undefined;
  const hex = body.slice(128, 128 + len * 2);
  if (hex.length !== len * 2) return undefined;
  let out = "";
  for (let i = 0; i < hex.length; i += 2) {
    out += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16));
  }
  return out || undefined;
}

/**
 * Walk a wagmi/viem error chain and pull out the raw revert `data` hex,
 * skipping viem's own "execution reverted" wrapper (no selector).
 * viem nests the real revert deep in the cause chain
 * (ContractFunctionExecutionError → ... → RpcRequestError), so we recurse
 * through every .cause / .innerError / .error until we find a hex string.
 */
function extractRevertData(e: any, depth = 0): string | undefined {
  if (!e || depth > 20) return undefined;
  if (typeof e.data === "string" && e.data.startsWith("0x") && e.data !== "0x") {
    return e.data;
  }
  for (const next of [e.cause, e.innerError, e.error]) {
    if (!next || next === e) continue;
    const found = extractRevertData(next, depth + 1);
    if (found) return found;
  }
  return undefined;
}

/**
 * Decode a caught contract error to a friendly message.
 *
 * Order of attempts:
 *  1. standard `Error(string)` reason (e.g. "!auctionEnded") — the common case
 *     for the auction facets' `require`s, and not covered by decodeErrorResult.
 *  2. custom `error` entry via the manifest ABI (e.g.
 *     "MintAmountExceedsMaxSupply", "TokenAlreadyExists(123)").
 *  3. viem's decoded `errorName`, when it is a real error name.
 *  4. the error's own message.
 *
 * viem's wrapper CLASS name (e.g. "ContractFunctionExecutionError") is never
 * used as a message — it carries no information and used to mask `e.message`.
 */
export function decodeRevert(e: any, contractName?: string): string {
  if (!e) return "Unknown error";
  const data = extractRevertData(e);

  if (data) {
    const reason = decodeErrorString(data);
    if (reason) return reason;

    const abi = contractName ? abiForContract(contractName) : undefined;
    if (abi) {
      try {
        const { errorName, args } = decodeErrorResult({ abi, data } as any);
        if (errorName) {
          const argStr = args && (args as any[]).length ? `(${args.join(", ")})` : "";
          return `${errorName}${argStr}`;
        }
      } catch {
        // not a custom error in this ABI — fall through
      }
    }
  }

  // viem may have decoded a real custom-error name already; trust it only when
  // it is not one of its own wrapper class names.
  const errName = (e as any)?.errorName;
  if (errName && !VIEM_WRAPPER_NAMES.has(errName)) return String(errName);

  return e?.message || String(e);
}
